import { useApi } from '@src/app/context/Api';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { Widget } from '@src/shared/ui/components/Widget';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { isEmpty, isUndefined } from 'lodash';
import React, { useEffect } from 'react';

import { Table } from './components/Table';

/**
 * Widget: CommonCriteria
 *
 * Displays a list of common criteria filters to apply to an api request.
 *
 * @param context
 * @param fetchCriteria
 * @param fetchCriteriaVersions
 * @param filterKey
 * @param form
 * @param isCollapsible
 * @param needsRemoval
 * @param needsReview
 * @param needsUpdate
 * @param setSelected
 * @param showActions
 * @returns {Element}
 * @constructor
 */
export function CommonCriteria({
  context = 'entrance',
  fetchCriteria = () => {},
  fetchCriteriaVersions = () => {},
  filterKey = 'criteria.entrance',
  form = {},
  isCollapsible = false,
  needsRemoval = {},
  needsReview = {},
  needsUpdate = {},
  setSelected = () => {},
  showActions = false,
}) {
  const { useApiQuery } = useApi('Criteria');

  const [ListCommonCriteria, { data, isFetching, isLoading }] = useApiQuery('ListCommonCriteria');

  /**
   * Fetch common criteria from the API.
   */
  useEffect(() => {
    if (isUndefined(data)) {
      ListCommonCriteria({
        isActive: true,
        page: 0,
        size: 9999,
        sort: 'name,asc',
      });
    }
  }, [data]);

  return (
    <ErrorHandler location="src/widgets/Criteria/CommonCriteria/CommonCriteria.jsx">
      <Widget
        backgroundColor="var(--abyss-colors-gray1)"
        collapsible={isCollapsible}
        icon="settings"
        title="Common Criteria"
      >
        <Grid>
          {isUndefined(data) || isLoading || isFetching ? (
            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              <Loader backgroundColor="transparent" height="100%" width="100%" />
            </Grid.Col>
          ) : (
            [
              <React.Fragment key="needsReview">
                {(!isEmpty(needsReview?.update) || !isEmpty(needsReview?.remove)) && (
                  <React.Fragment>
                    {!isEmpty(needsReview?.remove) && (
                      <Grid.Col key="removeAlert" span={{ xs: '100%' }}>
                        <Alert title="Deprecated Criteria" variant="warning">
                          {needsReview?.remove.join(', ')} {needsReview?.remove.length > 1 ? 'are' : 'is'} no longer
                          supported. Please click the remove button to advance the{' '}
                          {needsReview?.remove.length > 1 ? 'action paths' : 'action path'}.
                        </Alert>
                      </Grid.Col>
                    )}
                    {!isEmpty(needsReview?.update) && (
                      <Grid.Col key="updateAlert" span={{ xs: '100%' }}>
                        <Alert title="Updated Criteria" variant="warning">
                          {needsReview?.update.join(', ')} {needsReview?.update.length > 1 ? 'have' : 'has'} been
                          updated. Please click the update button to advance the{' '}
                          {needsReview?.update.length > 1 ? 'action paths' : 'action path'}.
                        </Alert>
                      </Grid.Col>
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>,

              <Grid.Col
                key="table"
                span={{
                  xs: '100%',
                }}
              >
                <Table
                  context={context}
                  fetchCriteria={fetchCriteria}
                  fetchCriteriaVersions={fetchCriteriaVersions}
                  filterKey={filterKey}
                  form={form}
                  needsRemoval={needsRemoval}
                  needsUpdate={needsUpdate}
                  rows={data?.content || []}
                  setSelected={setSelected}
                  showActions={showActions}
                />
              </Grid.Col>,
            ]
          )}
        </Grid>
      </Widget>
    </ErrorHandler>
  );
}
